import { useMemo } from 'react';

import { FeatureErrorBoundary } from '@/shared/components/FeatureErrorBoundary';
import { LiveStatus } from '@/shared/components/LiveStatus';
import { PageHeader } from '@/shared/components/PageHeader';
import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';
import { OVERVIEW_GRID, OVERVIEW_MAIN_COLUMN } from '../overviewLayout';
import { OverviewSkeleton } from './OverviewSkeleton';
import type { TaskView, WorkflowView } from '@/lib/api/generated/model';
import {
  CapacityMeter,
  WorkspaceConcurrencyProvider,
} from '@/features/concurrency';
import { useIsOrgAdmin } from '@/features/identity';
import { useWorkspace } from '@/features/workspaces';
import { boardEligibleWorkflows } from '@/features/workflows';
import {
  LIVE_POLL_MS,
  listWorkspaceTasks,
  hasNonTerminalActivity,
  summarizeWorkspaceTasks,
  useWorkspaceQueue,
} from '@/features/tasks';
import { InMotionSection } from './InMotionSection';
import { inFlight } from '@/features/workspace-overview/inFlight';
import { restOfWorkspace } from '@/features/workspace-overview/restOfWorkspace';
import { BoardsSection } from './BoardsSection';
import { RecentChangesSection } from './RecentChangesSection';
import { FirstRunState } from './FirstRunState';
import { isFirstRun } from '../firstRunSteps';
import { FinishedThisWeek } from './FinishedThisWeek';
import { SinceYouWereHere } from './SinceYouWereHere';
import { YourTurnSection } from './YourTurnSection';
import {
  UnreadableWorkflowsNotice,
  WorkspaceCounters,
} from './WorkspaceCounters';
import { ReadFailure } from '@/shared/components/ReadFailure';
import { usePreferencesStore } from '@/shared/store/preferences';

export interface OverviewPageProps {
  workspaceId: string;
}

/**
 * What is wrong or waiting in this workspace.
 *
 * Reads the same aggregate the board reads, under the same cache key, and
 * derives task state through the same path a card does — so the two surfaces
 * cannot describe one task differently. Nothing here writes: every control is
 * a link to a surface that already exists.
 */
export function OverviewPage({ workspaceId }: OverviewPageProps) {
  const { data: workspace, isPending } = useWorkspace(workspaceId);
  const isOrgAdmin = useIsOrgAdmin();
  // The whole workspace's line, not the narrowed one: the meter answers for the
  // workspace whatever the page is filtered to (spec 118).
  const queue = useWorkspaceQueue(workspace);

  // A second subscription to the same query, adding a refetch policy rather
  // than a second request — the shape `BoardPage` established. Conditional on
  // something actually being in flight, because unconditional polling of a
  // whole workspace is a constitution violation, not merely wasteful.
  const live = hasNonTerminalActivity(workspace?.tasks ?? []);
  const refresh = useWorkspace(workspaceId, {
    refetchInterval: live ? LIVE_POLL_MS : false,
    // An idle page reads again when the reader comes back (spec 126 R1).
    refetchOnWindowFocus: 'always',
  });

  /*
    No narrowing since spec 152: the page answers "what is my turn" for the
    whole workspace, and searching or filtering by label is the Tasks page's
    job. An old narrowed address is redirected there by the route.
  */
  const summary = useMemo(
    () =>
      summarizeWorkspaceTasks(
        workspace?.workflows ?? [],
        workspace?.tasks ?? [],
        workspace?.tasks ?? [],
      ),
    [workspace?.workflows, workspace?.tasks],
  );

  // Deeper padding at the foot than at the head. This page ends in its longest
  // list, and a list whose final row stops exactly at the bottom of the
  // scroller reads as truncated — the reader cannot tell the last board from a
  // board that was cut off. The extra space is the page saying it has finished.
  return (
    // The workspace's limit, for Active now's queue sentences (spec 116).
    <WorkspaceConcurrencyProvider limit={workspace?.concurrency?.limit}>
      <section className="flex flex-1 flex-col gap-5 p-6 max-md:px-4 max-md:pt-4 pb-12">
        <PageHeader
          surface="inverse"
          size="compact"
          title="Overview"
          // Whether the page is keeping itself current, where the Tasks page
          // says it (spec 122 FR-016). Only once the workspace is read.
          aside={
            workspace ? (
              <LiveStatus
                live={live}
                updatedAt={refresh.dataUpdatedAt}
                failed={refresh.isRefetchError}
                retrying={refresh.isFetching}
                onRetry={() => {
                  void refresh.refetch();
                }}
              />
            ) : undefined
          }
          // How busy the workspace is, always (spec 118 FR-011): it replaced both
          // the queue notice and the "N of M running" line under the totals.
          actions={
            <CapacityMeter
              capacity={workspace?.concurrency}
              workspaceId={workspaceId}
              queue={queue}
              isAdmin={isOrgAdmin}
            />
          }
        />

        <FeatureErrorBoundary fallbackMessage="This page could not be shown.">
          <OverviewBody
            workspaceId={workspaceId}
            isPending={isPending}
            hasWorkspace={workspace != null}
            onRetry={() => {
              void refresh.refetch();
            }}
            retrying={refresh.isFetching}
            summary={summary}
            workflows={workspace?.workflows ?? []}
            tasks={workspace?.tasks ?? []}
          />
        </FeatureErrorBoundary>
      </section>
    </WorkspaceConcurrencyProvider>
  );
}

interface OverviewBodyProps {
  workspaceId: string;
  isPending: boolean;
  hasWorkspace: boolean;
  onRetry: () => void;
  retrying: boolean;
  summary: ReturnType<typeof summarizeWorkspaceTasks>;
  workflows: WorkflowView[];
  /** Every task, so In motion can read a parent's batch (spec 150). */
  tasks: TaskView[];
}

/**
 * The page's four states, in the only order that keeps them disjoint.
 *
 * The failure branch is `!isPending && !hasWorkspace`, deliberately not
 * `isError`. `workspaceQueryOptions` resolves a non-200 to `null` instead of
 * throwing — so the shell can blank a name rather than error a page — which
 * means `isError` is *false* here on a read that failed. A page branching on it
 * would fall through to its empty state and tell the reader that a workspace
 * they cannot reach contains nothing.
 */
function OverviewBody({
  workspaceId,
  isPending,
  hasWorkspace,
  onRetry,
  retrying,
  summary,
  workflows,
  tasks,
}: OverviewBodyProps) {
  const showPlaceholder = usePendingDisplay(isPending);
  // Grouped once per read: each piece of work with its subtasks (spec 150).
  const tasksById = useMemo(
    () => new Map(tasks.map((task) => [task.id, task])),
    [tasks],
  );
  const workflowsById = useMemo(
    () =>
      new Map(
        workflows.map((workflow) => [
          workflow.id,
          { key: workflow.key, published: workflow.status === 'published' },
        ]),
      ),
    [workflows],
  );
  const flight = useMemo(
    () => inFlight(summary, tasksById),
    [summary, tasksById],
  );
  const setupDismissed = usePreferencesStore((state) =>
    state.dismissedSetup.includes(workspaceId),
  );

  // This page already kept its header outside the branch — the frame was never the
  // problem here (spec 079 FR-005 was satisfied before this feature touched it). All
  // that changes is the shape of what stands in the body, and that it waits out the
  // appearance delay first (FR-017).
  if (isPending) return showPlaceholder ? <OverviewSkeleton /> : null;

  if (!hasWorkspace) {
    return (
      <ReadFailure
        message="This workspace could not be read, so nothing on this page can be shown."
        onRetry={onRetry}
        isFetching={retrying}
      />
    );
  }

  // Nothing has been set up here at all. Note the *and*: a workspace with
  // workflows but no tasks has been set up and gets the ordinary page, where
  // the boards section is the useful thing to show.
  // Not started yet: nothing published and nothing to do (spec 099 FR-006).
  // A draft alone still shows the path, with its first step done; a published
  // workflow gets the ordinary page, where the boards section is the useful thing.
  if (isFirstRun(workflows, summary.counts.tasks) && !setupDismissed) {
    return (
      <FirstRunState
        workspaceId={workspaceId}
        workflows={workflows}
        taskCount={summary.counts.tasks}
      />
    );
  }

  // Published only — the count the totals panel reports. The *list* of boards
  // no longer comes from here: `summary.boards` already holds one row per
  // published workflow whose board built, ordered by what needs answering, so
  // narrowing a second time here would be a second opinion about which
  // workflows have a board.
  const published = boardEligibleWorkflows(workflows);

  return (
    /*
     * The lists on the left, the figures on the right.
     *
     * The page was five full-width bands stacked down a 1,296px column: an
     * Active-now row carried forty characters across the whole width, and the
     * empty half of Needs attention held a 636×418 hole at the top of the
     * screen — 37% of the first band, blank. It came to 1,615px in a 1,450px
     * viewport, so it always scrolled while a third of the first screen said
     * nothing.
     *
     * The split is by *kind*: the main column holds the two lists a reader acts
     * on, the rail holds the three things that describe the workspace rather
     * than asking anything of it. That is why the totals panel sits in the rail
     * — it reads as a summary beside the lists, not as a band interrupting
     * them, and it was designed narrow for exactly this slot.
     *
     * The cost, stated because it is a real one: the markup now reads
     * attention, active, totals, notice, boards, where the spec fixed the order
     * as attention, *totals*, active, notice, boards. Only the totals panel
     * moves, and only relative to Active now. That is a deliberate decision
     * taken by the spec's owner, not an oversight — a rail cannot have an
     * independent height without being its own element, so no CSS placement
     * keeps both the visual column and the original source order.
     */
    <div className={OVERVIEW_GRID}>
      <div className={OVERVIEW_MAIN_COLUMN}>
        {/* The checklist stays above the page until the first task exists
            (spec 128 FR-011), unless this person put it away — publishing
            a workflow is not the end of setting up. */}
        {summary.counts.tasks === 0 && !setupDismissed ? (
          <FirstRunState
            workspaceId={workspaceId}
            workflows={workflows}
            taskCount={summary.counts.tasks}
            compact
          />
        ) : null}

        {/* First, and above everything: "how much" is a question asked after
            "is anything wrong", and a summary on top would push the actionable
            list below the fold on exactly the workspaces where it is longest. */}
        {/* What happened while the person was away (spec 155 US1). */}
        <SinceYouWereHere
          workspaceId={workspaceId}
          attention={summary.attention}
          tasks={tasks}
        />

        <YourTurnSection
          workspaceId={workspaceId}
          attention={summary.attention}
          concentration={summary.concentration}
        />

        <InMotionSection
          workspaceId={workspaceId}
          flight={flight}
          tasksById={tasksById}
          workflows={workflowsById}
          running={summary.counts.running}
        />

        {/*
          Last, and in the main column rather than the rail.

          It belongs on this side by kind — it is a record of what happened,
          like the two lists above it, not a figure describing the workspace.
          It also balances: the rail already carries three sections and this
          column two, so adding a fourth section to the rail would have made
          the page taller than the layout just spent an entire pass shortening.

          It is the page's only second read, so it sits at the bottom where a
          failure to load costs the reader nothing above it.
        */}
        <RecentChangesSection workspaceId={workspaceId} />
      </div>

      <div className="flex min-w-0 flex-col gap-5">
        <WorkspaceCounters
          workspaceId={workspaceId}
          rest={restOfWorkspace(summary)}
          workflowTotal={workflows.length}
          publishedTotal={published.length}
          draftTotal={workflows.length - published.length}
        />

        {/* Directly under the figures it qualifies: "counted nowhere on this
            page" belongs next to the counts it is talking about. */}
        <UnreadableWorkflowsNotice
          workspaceId={workspaceId}
          workflows={summary.unreadableWorkflows}
        />

        {/* The one sign of momentum (spec 155 US3): what got done. */}
        <FinishedThisWeek
          workspaceId={workspaceId}
          rows={listWorkspaceTasks(workflows, tasks)}
        />

        <BoardsSection workspaceId={workspaceId} boards={summary.boards} />
      </div>
    </div>
  );
}
